import React from 'react';
import {
    Modal,
    Box,
    CloseButton,
    Button,
    Flex,
    FormControlInput,
    Heading,
    Divider,
    Text,
    CircularProgress,
} from '@shared/ui';
import { Picture } from 'app/components/Picture';
import i18n from '@shared/i18n';
import { CouponItem } from './CouponItem';

interface LaptopModalProps {
    visible: boolean;
    manualCouponCode: string;
    setManualCouponCode: (code: string) => void;
    error: string;
    isFetching: boolean;
    couponList: BuyerPromoCoupon[];
    selectedCouponId: string;
    isCartCalculating: boolean | undefined;
    couponSavingPrice: Price | undefined;
    handleSelectedCouponOnChange: (id: string) => void;
    enhanceCouponCodeOnSubmit: () => void;
    handleOnClose: () => void;
    emptyImageSrc: string;
}

export const LaptopModal: React.FC<LaptopModalProps> = ({
    visible,
    manualCouponCode,
    setManualCouponCode,
    error,
    isFetching,
    couponList,
    selectedCouponId,
    isCartCalculating,
    couponSavingPrice,
    handleSelectedCouponOnChange,
    enhanceCouponCodeOnSubmit,
    handleOnClose,
    emptyImageSrc,
}) => {
    return (
        <Modal
            sx={{ maxWidth: '381px' }}
            isOpen={visible}
            onClose={handleOnClose}
            closeOnOverlayClick
        >
            <Box p="m">
                <CloseButton
                    position="absolute"
                    top="16px"
                    left="16px"
                    w="16px"
                    h="16px"
                    onClick={handleOnClose}
                />
                <Heading
                    as="h3"
                    fontSize="sm"
                    mb="s"
                    fontWeight="bold"
                    textAlign="center"
                >
                    {i18n.gettext('優惠券與優惠碼')}
                </Heading>
                <Box
                    maxH={{
                        sm: 'unset',
                        md: '600px',
                    }}
                    overflow="scroll"
                >
                    <Divider color="primary.020" />
                    <Flex gap="s" my="xs" pl="1px">
                        <FormControlInput
                            height="40px"
                            placeholder={i18n.gettext('輸入優惠券碼')}
                            defaultValue={manualCouponCode}
                            onChange={setManualCouponCode}
                            error={error}
                        />
                        <Box w="fit-content">
                            <Button
                                isDisabled={!manualCouponCode}
                                onClick={enhanceCouponCodeOnSubmit}
                                h="40px !important"
                            >
                                {i18n.gettext('使用')}
                            </Button>
                        </Box>
                    </Flex>
                    {isFetching ? (
                        <Flex h="385px" justifyContent="center" alignItems="center">
                            <CircularProgress isIndeterminate />
                        </Flex>
                    ) : (
                        <>
                            {couponList.length ? (
                                <Flex minH="385px" gap="xs" flexDirection="column">
                                    {couponList.map((coupon, idx) => (
                                        <CouponItem
                                            key={`cart-coupon-${idx}-${coupon.id}`}
                                            coupon={coupon}
                                            isSelected={
                                                selectedCouponId === coupon.id &&
                                                (!isCartCalculating || !isFetching)
                                            }
                                            isCartCalculating={isCartCalculating}
                                            selectedCouponId={selectedCouponId}
                                            couponSavingPrice={couponSavingPrice}
                                            handleSelectedCouponOnChange={
                                                handleSelectedCouponOnChange
                                            }
                                        />
                                    ))}
                                </Flex>
                            ) : (
                                <Flex
                                    h="385px"
                                    flexDirection="column"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Box w="250px">
                                        <Picture defaultSrc={emptyImageSrc} alt="empty" />
                                    </Box>
                                    <Text color="neutral.100">
                                        {i18n.gettext('沒有可使用的優惠券')}
                                    </Text>
                                </Flex>
                            )}
                        </>
                    )}
                </Box>
                <Divider my="s" color="primary.020" />
                <Flex h="40px">
                    <Button onClick={handleOnClose}>
                        {!isCartCalculating && selectedCouponId
                            ? i18n.gettext('確認')
                            : i18n.gettext('關閉')}
                    </Button>
                </Flex>
            </Box>
        </Modal>
    );
};
